import {
  Dropdown,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  ThemeProvider,
} from "@enduring-systems/react-ui";
import {
  Activity,
  BarChart3,
  Bot,
  Building2,
  FileText,
  Flag,
  Gauge,
  Inbox,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  MessagesSquare,
  Radar,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAssistantAccess } from "@/feature-flags";
import { useAuth } from "@/lib/useAuth";

function getInitials(name: string, email: string): string {
  const fromName = name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2);
  if (fromName.length > 0) return fromName;
  return (email[0] ?? "?").toUpperCase();
}

function UserAvatar({
  src,
  name,
  email,
  className,
}: {
  src: string | undefined;
  name: string;
  email: string;
  className: string;
}): React.ReactElement {
  const [errored, setErrored] = useState(false);
  if (src && !errored) {
    return (
      <img
        src={src}
        alt=""
        referrerPolicy="no-referrer"
        className={className}
        onError={(): void => setErrored(true)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`${className} inline-flex items-center justify-center font-mono text-[10px] font-semibold uppercase tracking-wider`}
      style={{ background: "var(--es-terra-500)", color: "oklch(0.12 0 0)" }}
    >
      {getInitials(name, email)}
    </span>
  );
}

export function AppSidebar(): React.ReactElement {
  const location = useLocation();
  const { user, logout } = useAuth();
  const assistantEnabled = useAssistantAccess();

  return (
    <Sidebar data-testid="app-sidebar" className="es-sidebar-shell">
      <SidebarHeader className="shrink-0 border-b border-[color:var(--es-border-subtle)]">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <span
            className="font-['Exo_2'] inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-base font-bold"
            style={{ background: "var(--es-terra-500)", color: "oklch(0.12 0 0)" }}
            aria-hidden
          >
            E
          </span>
          <SidebarLabel className="font-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--es-fg-secondary)]">
            Endure / Ops
          </SidebarLabel>
        </div>
      </SidebarHeader>
      <SidebarContent className="es-sidebar-content">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/"}>
                  <Link to="/">
                    <LayoutDashboard />
                    <SidebarLabel>Dashboard</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/observability"}>
                  <Link to="/observability">
                    <Radar />
                    <SidebarLabel>Observability</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/waitlist"}>
                  <Link to="/waitlist">
                    <Users />
                    <SidebarLabel>Waitlist</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/feedback"}>
                  <Link to="/feedback">
                    <MessageSquare />
                    <SidebarLabel>Feedback</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/users"}>
                  <Link to="/users">
                    <UserCog />
                    <SidebarLabel>Users</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname.startsWith("/organization")}>
                  <Link to="/organization">
                    <Building2 />
                    <SidebarLabel>Organization</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/devices"}>
                  <Link to="/devices">
                    <Activity />
                    <SidebarLabel>Devices</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/feature-flags"}>
                  <Link to="/feature-flags">
                    <Flag />
                    <SidebarLabel>Feature Flags</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/entitlements"}>
                  <Link to="/entitlements">
                    <ShieldCheck />
                    <SidebarLabel>Entitlements</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {assistantEnabled && (
          <SidebarGroup data-testid="nav-assistant">
            <SidebarGroupLabel>Assistant</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/assistant"}>
                    <Link to="/assistant">
                      <Bot />
                      <SidebarLabel>Admin</SidebarLabel>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/usage-metrics"}>
                    <Link to="/usage-metrics">
                      <BarChart3 />
                      <SidebarLabel>Usage Metrics</SidebarLabel>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/conversation-logs"}>
                    <Link to="/conversation-logs">
                      <MessagesSquare />
                      <SidebarLabel>Conversation Logs</SidebarLabel>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/guardrail-metrics"}>
                    <Link to="/guardrail-metrics">
                      <ShieldCheck />
                      <SidebarLabel>Guardrail Metrics</SidebarLabel>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/rag-quality"}>
                    <Link to="/rag-quality">
                      <Gauge />
                      <SidebarLabel>RAG Quality</SidebarLabel>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={
                    location.pathname === "/audit-log" ||
                    location.pathname.startsWith("/audit-log/")
                  }
                >
                  <Link to="/audit-log">
                    <FileText />
                    <SidebarLabel>Audit Log</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={
                    location.pathname === "/test-inbox" ||
                    location.pathname.startsWith("/test-inbox/")
                  }
                >
                  <Link to="/test-inbox">
                    <Inbox />
                    <SidebarLabel>Test Inbox</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/settings"}>
                  <Link to="/settings">
                    <Settings />
                    <SidebarLabel>Settings</SidebarLabel>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="mt-auto shrink-0 border-t border-[color:var(--es-border-subtle)] bg-[color:var(--es-bg)]">
        <Dropdown>
          <Dropdown.Trigger asChild>
            <button
              type="button"
              data-testid="sidebar-user"
              className="es-sidebar-user-trigger flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors"
            >
              <UserAvatar
                src={user.picture}
                name={user.name}
                email={user.email}
                className="h-7 w-7 shrink-0 rounded-full object-cover"
              />
              <SidebarLabel className="min-w-0 flex-1">
                <div className="truncate font-mono text-[11px] text-[color:var(--es-fg)]">
                  {user.email}
                </div>
                <div className="truncate font-mono text-[10px] uppercase tracking-[0.04em] text-[color:var(--es-fg-muted)]">
                  Ops
                </div>
              </SidebarLabel>
            </button>
          </Dropdown.Trigger>
          {/* The portaled menu inherits its theme class from the nearest
           * ThemeProvider, which out here is the rail's ops-dark. Nest the
           * light ops provider so the design system stamps `.theme-ops` on
           * the menu itself and it pops against the dark rail, with no
           * hand-copied token values. */}
          <ThemeProvider theme="ops">
            <Dropdown.Menu>
              {/* Dropdown.Menu carries role="menu"; ARIA requires direct children
               * to be menuitem / separator / presentation. This user-summary
               * block is purely informational; marking it presentational keeps
               * screen readers from expecting activation semantics here. */}
              <div role="presentation" className="flex items-center gap-2.5 px-3 py-2">
                <UserAvatar
                  src={user.picture}
                  name={user.name}
                  email={user.email}
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-[color:var(--es-fg)]">
                    {user.name}
                  </div>
                  <div className="truncate text-xs text-[color:var(--es-fg-muted)]">
                    {user.email}
                  </div>
                </div>
              </div>
              <Dropdown.Separator />
              <Dropdown.Item onSelect={logout} data-testid="logout-button">
                <LogOut className="h-4 w-4" />
                Log out
              </Dropdown.Item>
            </Dropdown.Menu>
          </ThemeProvider>
        </Dropdown>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
